// oxlint-disable-next-line no-restricted-imports
import deburr from "lodash/deburr";
import isFunction from "lodash/isFunction";
import { EmojiRegex } from "./EmojiRegexHelper";

/** Locale-aware sorting and text normalization for display labels. */
export namespace SortHelper {
  /**
   * Sorts a copy by string values using the default locale, ignoring punctuation but retaining case sensitivity.
   *
   * @param items The array of items to sort.
   * @param predicate The property name or a function that returns the value to sort by.
   * @returns A new, sorted array.
   * @throws {Error} If a compared value is not a string.
   */
  export const natural = <T>(items: T[], predicate: keyof T | ((item: T) => string)) => {
    if (items.length < 2) {
      return items.slice();
    }

    const collator = new Intl.Collator(undefined, { ignorePunctuation: true });
    return items.slice().sort((a: T, b: T) => {
      const aValue = isFunction(predicate) ? predicate(a) : a[predicate];
      const bValue = isFunction(predicate) ? predicate(b) : b[predicate];

      if (typeof aValue === "string" && typeof bValue === "string") {
        return collator.compare(aValue, bValue);
      }

      throw new Error("Cannot sort by property " + String(predicate) + " of type " + typeof aValue);
    });
  };

  /**
   * Normalizes a string for sorting by removing diacritics and emojis.
   *
   * @param str The string to normalize.
   * @returns The normalized string.
   */
  export const normalizeString = (str: string) => {
    return deburr(EmojiRegex.removeEmojis(str.normalize("NFKD")));
  };
}
